import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const MEMBER_ROUTE_PREFIXES = ['/dashboard', '/profile', '/login', '/signup'];
const MEMBER_MENU_CLOSE_DELAY_MS = 220;

const MemberAreaControl = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const closeMenuTimeoutRef = useRef(null);

  const isAuthenticated = Boolean(user);
  const isMemberRouteActive = MEMBER_ROUTE_PREFIXES.some((routePrefix) => location.pathname.startsWith(routePrefix));

  const memberDisplayName = useMemo(() => {
    const profileUsername = profile?.username || profile?.display_name || profile?.full_name;
    const metadataUsername = user?.user_metadata?.username || user?.user_metadata?.display_name;
    const emailFallback = user?.email ? user.email.split('@')[0] : '';
    return profileUsername || metadataUsername || emailFallback || '';
  }, [profile, user]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => () => {
    if (closeMenuTimeoutRef.current) {
      window.clearTimeout(closeMenuTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated || !isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAuthenticated, isOpen]);

  const clearScheduledCloseMenu = () => {
    if (closeMenuTimeoutRef.current) {
      window.clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  };

  const openMenu = () => {
    clearScheduledCloseMenu();

    if (!isAuthenticated) {
      setIsOpen(true);
    }
  };

  const closeMenu = () => {
    clearScheduledCloseMenu();

    if (!isAuthenticated) {
      setIsOpen(false);
    }
  };

  const scheduleCloseMenu = () => {
    if (isAuthenticated) {
      return;
    }

    clearScheduledCloseMenu();
    closeMenuTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeMenuTimeoutRef.current = null;
    }, MEMBER_MENU_CLOSE_DELAY_MS);
  };

  const handleButtonClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    setIsOpen((prev) => !prev);
  };

  const handleBlurCapture = (event) => {
    if (!containerRef.current?.contains(event.relatedTarget)) {
      closeMenu();
    }
  };

  return (
    <div
      ref={containerRef}
      className="site-member-area"
      role="presentation"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleCloseMenu}
      onBlurCapture={handleBlurCapture}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleButtonClick}
        className={`member-area-button site-member-area-button ${isAuthenticated ? 'connected' : 'disconnected'} ${isMemberRouteActive ? 'active' : ''}`}
        aria-haspopup={isAuthenticated ? undefined : 'menu'}
        aria-expanded={isAuthenticated ? undefined : isOpen}
        aria-label={t('auth.dashboard.title', 'Espace membre')}
        title={t('auth.dashboard.title', 'Espace membre')}
      >
        <span className="site-member-area-icon" aria-hidden="true">
          <picture className="site-member-area-icon-white">
            <source srcSet="/images/buttons/webp/EspaceMembre_Blanc.webp" type="image/webp" />
            <img
              src="/images/buttons/EspaceMembre_Blanc.png"
              alt=""
              width="20"
              height="20"
            />
          </picture>
          <picture className="site-member-area-icon-black">
            <source srcSet="/images/buttons/webp/Espacemembre_NOIR.webp" type="image/webp" />
            <img
              src="/images/buttons/Espacemembre_NOIR.png"
              alt=""
              width="20"
              height="20"
            />
          </picture>
        </span>
        {isAuthenticated && memberDisplayName ? (
          <span className="member-greeting site-member-greeting site-member-area-label" aria-live="polite">
            {t('auth.dashboard.welcome', { name: memberDisplayName })}
          </span>
        ) : (
          <span className="site-member-area-label">{t('auth.dashboard.title', 'Espace membre')}</span>
        )}
        {!isAuthenticated && (
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`site-member-area-chevron ${isOpen ? 'open' : ''}`}
          />
        )}
      </button>

      {!isAuthenticated && (
        <div className={`member-area-dropdown ${isOpen ? 'open' : ''}`} role="menu" aria-label={t('auth.dashboard.title', 'Espace membre')}>
          <Link to="/signup" role="menuitem" className="member-area-dropdown-item primary" onClick={closeMenu}>
            {t('auth.signup.submit', "S'inscrire")}
          </Link>
          <Link to="/login" role="menuitem" className="member-area-dropdown-item" onClick={closeMenu}>
            {t('auth.login.submit', 'Se connecter')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default MemberAreaControl;
